import React, { useState } from 'react';
import type { View } from '../../types';
import { MoreIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from '../icons';
import { usePressRelease } from '../../lib/api/hooks/press-release';

interface PressReleaseDetailPageProps {
  pressReleaseId: string;
  setView: (view: View) => void;
}

const PressReleaseDetailPage: React.FC<PressReleaseDetailPageProps> = ({ pressReleaseId, setView }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const commentsPerPage = 5;

  const { data: pressReleaseData, isLoading, error } = usePressRelease(pressReleaseId);
  const article = pressReleaseData?.data;

  const mockComments: never[] = [];

  const totalPages = Math.ceil(mockComments.length / commentsPerPage);
  const currentComments = mockComments.slice(
    (currentPage - 1) * commentsPerPage,
    currentPage * commentsPerPage
  );

  const goToPage = (pageNumber: number) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, pageNumber)));
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading press release...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Press Release not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The press release could not be found'}</p>
        <button onClick={() => setView({ page: 'Press Release' })} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Press Releases
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-500">
        <span className="cursor-pointer hover:underline" onClick={() => setView({ page: 'Press Release' })}>Press Release</span>
        <span className="mx-2">&gt;</span>
        <span className="font-medium text-gray-700">{article.title}</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
        <div className="flex items-start gap-6">
          {article.coverImage && (
            <img src={article.coverImage} alt={article.title} className="w-40 h-28 object-cover rounded-lg" />
          )}
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <div>
                 <span className="text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                   {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                 </span>
                 <h2 className="text-2xl font-bold text-gray-800 mt-2">{article.title}</h2>
              </div>
              <button className="text-gray-500 hover:text-gray-800"><MoreIcon /></button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Posted {formatDate(article.createdAt)} | Posted by {article.createdBy}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Settings</h3>
            <div className="space-y-2">
                <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.landingPage}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Landing page/Hero Section</span>
                </label>
                 <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.pressRelease}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Press Release</span>
                </label>
                 <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.pressReleaseBanner}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Press Release Banner</span>
                </label>
                 <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.allowComment}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Allow Comments</span>
                </label>
            </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Cover Image Title</h3>
            <p className="text-sm text-gray-700">{article.coverImageTitle || 'No cover image title'}</p>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Article Tags</h3>
            <div className="flex flex-wrap gap-2">
              {article.articleTag ? (
                <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                  {article.articleTag}
                </span>
              ) : (
                <span className="text-sm text-gray-500">No tags</span>
              )}
            </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Content</h3>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="p-6 flex justify-between items-center border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-800">Comments</h3>
            <button className="flex items-center gap-2 text-sm font-medium text-gray-600">
                Today <ChevronDownIcon className="w-4 h-4" />
            </button>
        </div>
        <div className="space-y-4 p-6">
            {currentComments.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No comments yet</p>
            )}
            {currentComments.map(comment => (
                <div key={(comment as any).id} className="pb-4 border-b border-gray-100 last:border-b-0">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="font-semibold text-gray-800">{(comment as any).author}</p>
                            <p className="text-xs text-gray-500">{(comment as any).email}</p>
                        </div>
                        <button className="text-gray-500 hover:text-gray-800"><MoreIcon /></button>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">{(comment as any).text}</p>
                </div>
            ))}
        </div>
        {totalPages > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-1">
                  <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-200/20 disabled:opacity-50">
                      <ChevronLeftIcon className="w-5 h-5 text-gray-600"/>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 3).map(pageNumber => (
                      <button
                          key={pageNumber}
                          onClick={() => goToPage(pageNumber)}
                          className={`px-3.5 py-1.5 rounded-md text-sm font-medium ${currentPage === pageNumber ? 'bg-[#681628] text-white' : 'bg-white text-gray-700 hover:bg-gray-200/20'}`}
                      >
                          {pageNumber}
                      </button>
                  ))}
                  {totalPages > 4 && <span className="px-2">...</span>}
                   {totalPages > 3 && (
                      <button
                          onClick={() => goToPage(totalPages)}
                          className={`px-3.5 py-1.5 rounded-md text-sm font-medium ${currentPage === totalPages ? 'bg-[#681628] text-white' : 'bg-white text-gray-700 hover:bg-gray-200/20'}`}
                      >
                          {totalPages}
                      </button>
                  )}
                  <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-200/20 disabled:opacity-50">
                      <ChevronRightIcon className="w-5 h-5 text-gray-600"/>
                  </button>
              </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PressReleaseDetailPage;
